import React, { useRef, useState } from "react";
import "./Signup.css"
import {
    Heading,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Container,
    VStack, Text, Button, Spacer, Image, Divider, Input, FormControl, InputRightElement, InputGroup, useToast,
    FormErrorMessage,
    FormLabel,
    ToastId
} from "@chakra-ui/react";
import { AiFillWechat, AiOutlineMail } from 'react-icons/ai'
import { Link, useSearchParams } from "react-router-dom";
import ToffeeTitle from "../toffee-title.svg"
import { signup, SignupRequest } from "../interface/user";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

function Signup(props: any) {
    const toast = useToast();
    
    const [ searchParams ] = useSearchParams();
    const phase = searchParams.get("phase");

    const [ isLoading, setIsLoading ] = useState(false);
    const [ passwordShown, setPasswordShown ] = useState(false);

    const schema = yup.object().shape({
        email: yup.string().email().required(),
        password: yup.string().min(8).required(),
        nickname: yup.string().required()
    });

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const toastRef = useRef<ToastId>(null);

    const onSubmit = (form: SignupRequest) => {
        setIsLoading(true);
        toastRef.current = toast({
            status: "loading",
            title: "Creating account...",
            description: "Please wait while we create your account",
        });
        signup(form).then(response => {
            console.log(response);
            toast.update(toastRef.current, {
                status: "success",
                title: "Account created successfully",
                description: "You can now login to your account",
            });
        }).catch(error => {
            if (error.response.status === 409) {
                toast.update(toastRef.current, {
                    status: "error",
                    title: "Email already exists",
                    description: "Please use a different email address",
                });
            } else {
                toast.update(toastRef.current, {
                    status: "error",
                    title: "Failed to create account",
                    description: "Please try again later",
                });
            }
        }).finally(() => {
            setIsLoading(false);
        });
    }

    return (
        <Container id={'login-page'} maxW={500}>
            <VStack spacing={6}>
                <Card variant={"elevated"} minW={400}>
                    <CardHeader>
                        <VStack spacing={3}>
                            <Link to={"/"}>
                                <Image
                                    htmlWidth={150}
                                    src={ToffeeTitle}
                                />
                            </Link>
                            <Heading size={"md"}>Welcome</Heading>
                            <Divider/>
                        </VStack>
                    </CardHeader>
                    <CardBody>
                        {phase === null && (
                            <VStack spacing={8}>
                                <Button variant={"outline"} leftIcon={<AiFillWechat/>}>
                                    Register with Wechat
                                </Button>
                                <Button variant={"outline"} leftIcon={<AiOutlineMail/>}>
                                    <Link to={"/signup?phase=email"}>
                                        Register with Email
                                    </Link>
                                </Button>
                            </VStack>
                        )}
                        {phase === "email" && (
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <VStack spacing={8}>
                                    <FormControl id={"Email"} isInvalid={!!errors.email}>
                                        <FormLabel>Email</FormLabel>
                                    <Input 
                                        {...register("email")}
                                        placeholder={"Email Address"}
                                    />
                                    {errors.email && <FormErrorMessage>{errors.email.message}</FormErrorMessage>}
                                    </FormControl>
                                    <FormControl id={"Password"} isInvalid={!!errors.password}>
                                        <FormLabel>Password</FormLabel>
                                        <InputGroup>
                                            <Input 
                                                {...register("password")}
                                                type={passwordShown ? "text" : "password"} 
                                                placeholder={"Password"}
                                            />
                                            <InputRightElement width={"4.5rem"}>
                                                <Button size={"sm"} onClick={() => setPasswordShown(!passwordShown)}>
                                                    {passwordShown ? "Hide" : "Show"}
                                                </Button>
                                            </InputRightElement>
                                        </InputGroup>
                                        {errors.password && <FormErrorMessage>{errors.password.message}</FormErrorMessage>}
                                    </FormControl>
                                    <FormControl id={"Nickname"} isInvalid={!!errors.nickname}>
                                        <FormLabel>Nickname</FormLabel>
                                        <Input 
                                            {...register("nickname")}
                                            placeholder={"Nickname"}
                                        />
                                        {errors.nickname && <FormErrorMessage>{errors.nickname.message}</FormErrorMessage>}
                                    </FormControl>
                                    <Button 
                                        colorScheme={"teal"} 
                                        width={"100%"} 
                                        isLoading={isLoading}
                                        type={"submit"}
                                    >
                                        Create Account
                                    </Button>
                                </VStack>
                            </form>
                        )}
                    </CardBody>
                    <CardFooter>
                        <Spacer/>
                        <Text paddingX={2}>Already have an account?</Text>
                        <Link to={"/login"}>
                            Login
                        </Link>
                    </CardFooter>
                </Card>
                <Container>
                    <Text fontSize={"sm"}>
                        When you link your Facebook, Google, or Yahoo account, Kaggle collects certain information stored in that account that you have configured to make available. By linking your accounts, you authorize Kaggle to access and use your account on the third party service in connection with your use of kaggle.com.
                    </Text>
                </Container>
            </VStack>
        </Container>
    );
}

export default Signup;